var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Button } from "@animech/ui";
import * as Styled from "./graph.style";
import { AirConsumptionGraph as AirConsumptionGraph } from "./air-consumption-graph";
import { RobotMotionGraph as RobotMotionGraph } from "./robot-motion-graph";
import { VacuumPumpGraph } from "./vacuum-pump-graph";
import { ChartBackground } from "./common/custom-chart-background";
import { LiftingForcesGraph } from "ui/components/panels/graphs/lifting-forces-graph";
import { actions, selectors, useAppDispatch, useAppSelector } from "store";
import { useBlockingElementEffect } from "hooks/playcanvas/blocking-element-effect";
import { InfoButton } from "ui/components/elements/info-button";
import { NodeList } from "ui/components/panels/graphs/common/node-list";
import { useGraphDataContext } from "providers/graph-panel";
import { CalculateButton } from "ui/components/panels/topbar/calculate-button";
import { LoadingOverlay } from "ui/components/elements/loading-overlay";
import { PlayLine } from "ui/components/panels/graphs/common/play-simulation/play-line";
import { useTimeDataMinMaxDomain } from "hooks/graph-panel/graph";
import { CupAnimation2D } from "ui/components/panels/dashboard/boxes/application-speed/cup-animation-2d";
import { GraphAnimationInfoBox } from "ui/components/panels/graphs/graph-animation-info-box";
import { useChartElementPositions } from "ui/components/elements/chart/use-chart-element-positions";
import { getGraphPlayLinePercent } from "ui/components/panels/graphs/play-line-position";
var CloseButton = function () {
    var dispatch = useAppDispatch();
    return (_jsx(Button, { appearance: "text", color: "inverted", icon: "close", rounded: true, size: "default", onClick: function () { return dispatch(actions.toggleGraphPanel(false)); } }));
};
var DataInvalid = function () {
    var t = useTranslation().t;
    return (_jsx(Styled.DataInvalid, { children: _jsxs(Styled.DataInvalidContent, { children: [_jsx(Styled.DataInvalidClose, { children: _jsx(CloseButton, {}) }), _jsx("p", { children: t("graph_panel.data_invalid_sentence_1") }), _jsx("p", { children: t("graph_panel.data_invalid_sentence_2") }), _jsx(CalculateButton, {})] }) }));
};
export var GraphPanel = function () {
    var t = useTranslation().t;
    var printModeActive = useGraphDataContext().printModeActive;
    var isGraphPanelOpen = useAppSelector(selectors.selectIsGraphPanelOpen);
    var isDataLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var isDataValid = useAppSelector(selectors.selectIsPerformanceDataValid);
    var _a = __read(useState(false), 2), shouldRenderGraphData = _a[0], setShouldRenderGraphData = _a[1];
    var showLoadingPanel = !isDataValid && isDataLoading;
    var showOutdatedDataWarning = shouldRenderGraphData && !isDataValid && !showLoadingPanel;
    var onTransitionEndEvent = function (e) {
        if (e.propertyName !== "transform")
            return;
        if (isGraphPanelOpen)
            return;
        setShouldRenderGraphData(false);
    };
    useEffect(function () {
        if (!isGraphPanelOpen)
            return;
        setShouldRenderGraphData(true);
    }, [isGraphPanelOpen]);
    return (_jsxs(Styled.GraphPanel, { isOpen: isGraphPanelOpen, printMode: printModeActive, onTransitionEnd: onTransitionEndEvent, children: [showOutdatedDataWarning && _jsx(DataInvalid, {}), showLoadingPanel && _jsx(LoadingOverlay, {}), _jsx(Styled.TopBar, { children: _jsxs(Styled.ButtonsContainer, { children: [_jsx(InfoButton, { title: t("dashboard.application_cycle_data"), description: {
                                long: t("descriptions.dashboard.application_cycle_data"),
                            } }), _jsx(CloseButton, {})] }) }), shouldRenderGraphData && _jsx(GraphDataContainer, {})] }));
};
var GraphDataContainer = function () {
    var t = useTranslation().t;
    var playLocationPercent = useAppSelector(selectors.selectPlayLocationPercent);
    var graphPanelRef = useGraphDataContext().graphPanelRef;
    useBlockingElementEffect();
    var xAxisDomain = useTimeDataMinMaxDomain();
    var _a = useGraphDataContext(), zoomedTimeDomain = _a.zoomedTimeDomain, airConsumptionGraphRef = _a.airConsumptionGraphRef;
    var chartElementPositionsData = useChartElementPositions();
    // The stored play position belongs to the application-speed timeline, where
    // before-pick phases happen before graph time starts at Pick.
    var zoomAdjustedPlayPercent = getGraphPlayLinePercent({
        playLocationPercent: playLocationPercent,
        playLocationTime: chartElementPositionsData
            ? chartElementPositionsData.percentToDisplayTime(playLocationPercent)
            : undefined,
        xAxisDomain: xAxisDomain,
        zoomedTimeDomain: zoomedTimeDomain,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var airGraphRef = airConsumptionGraphRef === null || airConsumptionGraphRef === void 0 ? void 0 : airConsumptionGraphRef.current;
    var drawingAreaLeft = airGraphRef === null || airGraphRef === void 0 ? void 0 : airGraphRef.state.offset.left;
    var drawingAreaWidth = airGraphRef === null || airGraphRef === void 0 ? void 0 : airGraphRef.state.offset.width;
    return (_jsx(Styled.GraphContainer, { ref: graphPanelRef, children: _jsxs(Styled.GraphContainerContent, { children: [_jsx(NodeList, {}), _jsxs(Styled.GraphSection, { children: [_jsx(ChartBackground, { children: _jsx(RobotMotionGraph, {}) }), _jsx(ChartBackground, { children: _jsx(AirConsumptionGraph, {}) }), _jsx(ChartBackground, { children: _jsx(VacuumPumpGraph, {}) }), _jsx(ChartBackground, { marginBottom: 60, children: _jsx(LiftingForcesGraph, {}) }), zoomAdjustedPlayPercent !== undefined && (_jsx(PlayLine, { playLocationPercent: zoomAdjustedPlayPercent })), zoomedTimeDomain &&
                            drawingAreaLeft !== undefined &&
                            drawingAreaWidth !== undefined && (_jsx(Styled.ZoomBorderOverlay, { left: drawingAreaLeft, width: drawingAreaWidth }))] }), _jsxs(Styled.CupAnimationContainer, { children: [_jsx(Styled.CupAnimationHeader, { children: t("graph_panel.cup-animation") }), _jsx(CupAnimation2D, {}), _jsx(GraphAnimationInfoBox, {})] })] }) }));
};
